import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { User } from "../models/User";
import apiClient from "../api/axios";
import alertService from "../components/alert/alertService";
import { handleApiError } from "../api/handleApiError";
import { useTranslation } from "react-i18next";
import { account, common } from "../i18n";

export interface AccountState {
  user?: User;
  updateUser: (
    name: string | null,
    displayName: string | null,
    phoneNumber: string | null,
    jobTitle: string | null,
    userProfile: string | null,
  ) => Promise<boolean>;
}

export const AccountContext = createContext<AccountState | undefined>(
  undefined,
);

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error("useAccount must be used within an AccountProvider");
  }
  return context;
};

export function AccountProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User>();

  const { t } = useTranslation([common, account], {
    nsMode: "fallback",
  });

  const getUser = async () => {
    alertService.showLoading();
    try {
      const response = await apiClient.get("/v1/users/info");
      if (response.status === 200) {
        setUser(response.data.results.user);
      }
    } catch (error) {
      try { handleApiError(error, {}, { messageKey: "error-get-user", titleKey: "Error-Message" }); } catch { /* alert already shown — prevent unhandled rejection */ }
    } finally {
      alertService.closeLoading();
    }
  };

  const updateUser = async (
    name: string | null,
    displayName: string | null,
    phoneNumber: string | null,
    jobTitle: string | null,
    userProfile: string | null,
  ) => {
    try {
      let body: any = {
        displayName: displayName ?? user?.displayName,
        fullname: name ?? "",
        phoneNumber: phoneNumber ?? "",
        jobTitle: jobTitle ?? "",
      };

      if (userProfile) {
        body.userProfile = userProfile;
      }

      const response = await apiClient.patch(`/v1/users/info`, body);
      if (response.status === 200) {
        // await getUser();

        // getUserInfo();

        setUser(response.data.results.user);

        alertService.mixin(t("edit-successfully"));

        return true;
      }
    } catch (error) {
      try { handleUpdateError(error); } catch { /* alert already shown — prevent unhandled rejection */ }
    }
    return false;
  };

  const handleUpdateError = (error: unknown) =>
    handleApiError(error, {
      FORBIDDEN_ACCESS_ERROR: "Cannot-Change-Organization-Due-To-Permission-Error",
      VALIDATION_ERROR: {
        messageKey: (body: string) => {
          const b = body.toLowerCase();
          if (b.includes("phonenumber")) return "Phone";
          if (b.includes("jobtitle")) return "Job-Title";
          if (b.includes("fullname")) return "Name";
          return "validation-failed";
        },
        titleKey: "validation-failed",
      },
    }, { messageKey: "error-edit-user", titleKey: "Error-Message" });

  useEffect(() => {
    getUser();
  }, []);

  const context = {
    user,
    updateUser,
  };

  return (
    <AccountContext.Provider value={context}>
      {children}
    </AccountContext.Provider>
  );
}
